import { DateHelpers } from "../helpers/date-helpers";
import { FormatHelpers } from "../helpers/format-helpers";
import { StringHelpers } from "../helpers/string-helpers";
import { TimeframeHelper } from "../helpers/timeframe-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { TIChartPointModel } from "../models/data-point/ti-chart-point.model";
import { TIIndicatorDataModel } from "../models/data-point/ti-indicator-data.model";
import { TIIndicatorPointModel } from "../models/data-point/ti-indicator-point.model";
import { IComponent } from "./component.interface";
import { EMACloudBuySellSummaryPopupComponent, TradeReportType } from "./ema-cloud-buy-sell-summary-popup.component";

export interface BuySellItem {
    index: number;
    entryBarTiDataPoint: TIChartPointModel | null;
    entryBarDate: Date | null;
    entryBarPrice: number | null;
    entryBarTooltip: string;
    exitBarDate: Date | null;
    exitBarPrice: number | null;
    exitBarTooltip: string;
    exitBarTiDataPoint: TIChartPointModel | null;
    profitPercentage: number | null;
    openProfitPercentage: number | null;
    added: boolean;
    isLong: boolean;
}

export interface emaCloud_Long_validateEmaFlagResult {
    hasGoodReversal: boolean;
    hasBadReversal: boolean;
    closeIsAboveCloud2AndCloud3: boolean;
    closeIsBelowCloud2AndCloud3: boolean;
    alreadyHasBuyOrSellWithinFiveBars: boolean;
    shouldBuy: boolean;
    shouldSell: boolean;
}

export class DrawEMACloudBuySellArrowComponent implements IComponent {
    public id = '';

    private base: IChartContainer;
    private arrows: Array<any> = [];
    private shortEntryExitItems: Array<BuySellItem> = [];
    private shortEntryExitTotalProfit = 0;
    private longEntryExitItems: Array<BuySellItem> = [];
    private longEntryExitTotalProfit = 0;
    private reportType: TradeReportType | null = null;
    private canDraw = false;

    private mouseHover = false;
    private mouseHoverTimer: any = null;

    private mouseMove = false;
    private mouseMoveTimer: any = null;

    constructor(base: IChartContainer) {
        this.base = base;

        this.base.tiChartSeries?.subscribeExtremeChange(this.id, () => {
            this.mouseMove = false;
            this.mouseHover = false;
            this.draw(this.reportType);
        });
        this.base.tiChartSeries?.subscribeRedrawChange(this.id, () => {
            this.draw(this.reportType);
        });
        this.base.chartWrapper.addEventListener('mousemove', () => {
            this.mouseMove = true;
            clearTimeout(this.mouseMoveTimer);
            this.mouseMoveTimer = setTimeout(() => {
                this.mouseMove = false;
            }, 1500);
        });
    }

    public enable() {
        this.canDraw = true;
        this.draw(null);
    }

    public disable() {
        this.canDraw = false;
        this.removeAllArrows();
    }

    public emaCloud_Long_validateEmaFlag(date: Date): emaCloud_Long_validateEmaFlagResult | null {
        const dateToMarketTimeZone = DateHelpers.toMarketTimeZone(date);
        const dataPoint = this.base.tiChartSeries?.tiChartPoints?.find((x: TIChartPointModel) => x.start.valueOf() == dateToMarketTimeZone.valueOf());
        if (!dataPoint) return null;

        const emaCloudSeriesResult = this.getEMACloudTwoSeries();
        if (!emaCloudSeriesResult) return null;

        const emaCloudTwo = emaCloudSeriesResult;
        const arrows = this.getEMACloudArrowsForLong(emaCloudTwo);

        let hasGoodReversal = false;
        let hasBadReversal = false;
        let closeIsAboveCloud2 = false;
        let closeIsBelowCloud2 = false;
        let alreadyHasBuyOrSellWithinFiveBars = false;
        let shouldBuy = false;
        let shouldSell = false;
        if (arrows.pointsToAddEnterArrow.some((x: any) => x.x == dataPoint.xValue)) {
            shouldBuy = true;
        } else if (arrows.pointsToAddExitArrow.some((x: any) => x.x == dataPoint.xValue)) {
            shouldSell = true;
        }

        if (arrows.goodReversalPoints.some((x: any) => x.x == dataPoint.xValue)) {
            hasGoodReversal = true;
        } else if (arrows.badReversalPoints.some((x: any) => x.x == dataPoint.xValue)) {
            hasBadReversal = true;
        }

        const emaCloudTwoPoint = emaCloudTwo?.points?.find(x => x.xValue == dataPoint.xValue)?.value;
        if (!isNaN(emaCloudTwoPoint)) {
            closeIsAboveCloud2 = this.closeIsAboveCloud2(dataPoint, emaCloudTwoPoint);
            closeIsBelowCloud2 = this.closeIsBelowCloud2(dataPoint, emaCloudTwoPoint);
        }

        if (arrows.pointsWithInFiveBars.some((x: any) => x.x == dataPoint.xValue)) {
            alreadyHasBuyOrSellWithinFiveBars = true;
        }

        return {
            hasGoodReversal: hasGoodReversal,
            hasBadReversal: hasBadReversal,
            closeIsAboveCloud2AndCloud3: closeIsAboveCloud2,
            closeIsBelowCloud2AndCloud3: closeIsBelowCloud2,
            alreadyHasBuyOrSellWithinFiveBars: alreadyHasBuyOrSellWithinFiveBars,
            shouldBuy: shouldBuy,
            shouldSell: shouldSell,
        }
    }

    public emaCloud_Short_validateEmaFlag(date: Date): emaCloud_Long_validateEmaFlagResult | null {
        const dateToMarketTimeZone = DateHelpers.toMarketTimeZone(date);
        const dataPoint = this.base.tiChartSeries?.tiChartPoints?.find((x: TIChartPointModel) => x.start.valueOf() == dateToMarketTimeZone.valueOf());
        if (!dataPoint) return null;

        const emaCloudSeriesResult = this.getEMACloudTwoSeries();
        if (!emaCloudSeriesResult) return null;

        const emaCloudTwo = emaCloudSeriesResult;
        const arrows = this.getEMACloudArrowsForShort(emaCloudTwo);

        let hasGoodReversal = false;
        let hasBadReversal = false;
        let closeIsAboveCloud2 = false;
        let closeIsBelowCloud2 = false;
        let alreadyHasBuyOrSellWithinFiveBars = false;
        let shouldBuy = false;
        let shouldSell = false;
        if (arrows.pointsToAddEnterArrow.some((x: any) => x.x == dataPoint.xValue)) {
            shouldBuy = true;
        } else if (arrows.pointsToAddExitArrow.some((x: any) => x.x == dataPoint.xValue)) {
            shouldSell = true;
        }

        if (arrows.goodReversalPoints.some((x: any) => x.x == dataPoint.xValue)) {
            hasGoodReversal = true;
        } else if (arrows.badReversalPoints.some((x: any) => x.x == dataPoint.xValue)) {
            hasBadReversal = true;
        }

        const emaCloudTwoPoint = emaCloudTwo?.points?.find(x => x.xValue == dataPoint.xValue)?.value;
        if (!isNaN(emaCloudTwoPoint)) {
            closeIsAboveCloud2 = this.closeIsAboveCloud2(dataPoint, emaCloudTwoPoint);
            closeIsBelowCloud2 = this.closeIsBelowCloud2(dataPoint, emaCloudTwoPoint);
        }

        if (arrows.pointsWithInFiveBars.some((x: any) => x.x == dataPoint.xValue)) {
            alreadyHasBuyOrSellWithinFiveBars = true;
        }

        return {
            hasGoodReversal: hasGoodReversal,
            hasBadReversal: hasBadReversal,
            closeIsAboveCloud2AndCloud3: closeIsAboveCloud2,
            closeIsBelowCloud2AndCloud3: closeIsBelowCloud2,
            alreadyHasBuyOrSellWithinFiveBars: alreadyHasBuyOrSellWithinFiveBars,
            shouldBuy: shouldBuy,
            shouldSell: shouldSell,
        }
    }

    public draw(reportType: TradeReportType | null) {
        if (!this.base.tiChartSeries?.highStockChart || !this.canDraw || !this.base.tiChartSeries?.chartStyleSettings?.emaCloudDisplayBuySellFlags) return;

        this.removeAllArrows();
        this.calculateShortEntryExitItems();
        this.calculateLongEntryExitItems();

        let longItems = this.longEntryExitItems;
        let shortItems = this.shortEntryExitItems;
        if (this.base.tiChartSeries.chartStyleSettings.emaCloudDisplayOnlyLastTwoTrades) {
            if (longItems.length) {
                longItems = longItems.slice(-2);

                this.longEntryExitTotalProfit = 0;
                longItems.forEach(x => {
                    if (x.profitPercentage != null) {
                        this.longEntryExitTotalProfit += parseFloat(x.profitPercentage.toFixed(2));
                    } else if (x.openProfitPercentage != null) {
                        this.longEntryExitTotalProfit += parseFloat(x.openProfitPercentage.toFixed(2));
                    }
                });
            }
            
            if (shortItems.length) {
                shortItems = shortItems.slice(-2);

                this.shortEntryExitTotalProfit = 0;
                shortItems.forEach(x => {
                    if (x.profitPercentage != null) {
                        this.shortEntryExitTotalProfit += parseFloat(x.profitPercentage.toFixed(2));
                    } else if (x.openProfitPercentage != null) {
                        this.shortEntryExitTotalProfit += parseFloat(x.openProfitPercentage.toFixed(2));
                    }
                });
            }
        }

        let isMouseMoveOrHover = this.mouseHover || this.mouseMove;
        if (reportType == null) {
            this.reportType = TradeReportType.Long;
            if (this.shortEntryExitTotalProfit > this.longEntryExitTotalProfit) {
                this.reportType = TradeReportType.Short;
            }
        } else if (this.reportType != reportType) {
            this.reportType = reportType;
            isMouseMoveOrHover = false;
        }

        if (isMouseMoveOrHover) return;

        const isLong = this.reportType == TradeReportType.Long;

        let tradeItems = isLong ? this.longEntryExitItems : this.shortEntryExitItems;
        const totalProfit = isLong ? this.longEntryExitTotalProfit : this.shortEntryExitTotalProfit;

        const series = this.base.tiChartSeries.highStockChart?.series?.[0];
        const xAxis = this.base.tiChartSeries.highStockChart?.xAxis?.[0];
        const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        const renderer = this.base.tiChartSeries.highStockChart?.renderer;

        if (!series || !xAxis || !renderer || !tradeItems.length) {
            (this.base.getComponentsByType(EMACloudBuySellSummaryPopupComponent)?.[0] as EMACloudBuySellSummaryPopupComponent)?.setData(this.reportType, [], 0);
            return;
        }

        if (this.base.tiChartSeries.chartStyleSettings.emaCloudDisplayOnlyLastTwoTrades) {
            if (tradeItems.length > 1) {
                tradeItems = [tradeItems[tradeItems.length - 2], tradeItems[tradeItems.length - 1]];
            } else if (tradeItems.length > 0) {
                tradeItems = [tradeItems[tradeItems.length - 1]];
            }
        }

        for (let i = 0; i < tradeItems.length; i++) {
            const item = tradeItems[i];
            if (!item.entryBarTiDataPoint) continue;

            let entryArrowLeft = xAxis?.toPixels(item.entryBarTiDataPoint.xValue) - 12;
            let entryArrowYValue = isLong ? item.entryBarTiDataPoint.low : item.entryBarTiDataPoint.high;
            let entryArrowTop = isLong ? this.getElementTop(item.entryBarTiDataPoint.low, true) : this.getElementTop(item.entryBarTiDataPoint.high, false);
            const entryArrowIcon = isLong ? this.getBuyIcon() : this.getSellIcon();
            const entryArrowKey = StringHelpers.newGuid();
            const entryArrowG = this.base.tiChartSeries.highStockChart.renderer.g(entryArrowKey).attr({
                zIndex: 13
            });

            if (entryArrowYValue > yAxis.min && entryArrowYValue < yAxis.max) {
                renderer.image(entryArrowIcon, entryArrowLeft, entryArrowTop, 24, 24)
                    .attr({
                        zIndex: 9
                    })
                    .add(entryArrowG);
    
                const entryArrowTitleElement = renderer.createElement('title');
                entryArrowTitleElement.add(entryArrowG);
    
                try {
                    const existingEntryArrowIndex = this.arrows.findIndex(x => x.id == entryArrowKey);
                    if (existingEntryArrowIndex > -1) {
                        this.arrows[existingEntryArrowIndex].g?.destroy();
    
                        entryArrowG.add();
                        this.arrows[existingEntryArrowIndex].g = entryArrowG;
                    } else {
                        entryArrowG.add();
    
                        this.arrows.push({
                            id: entryArrowKey,
                            g: entryArrowG
                        })
                    }
                } catch { }
    
                setTimeout(() => {
                    const entryTitle = this.base.chartWrapper.querySelector('.highcharts-' + entryArrowKey + ' title');
                    if (entryTitle) {
                        // Add a Buy Tooltip - "Buy @ 2.46, on 7/25/2024 1:00PM"
                        entryTitle.innerHTML = item.entryBarTooltip;
                    }
    
                }, 500);
            }

            if (item.exitBarTiDataPoint) {
                let exitArrowLeft = xAxis?.toPixels(item.exitBarTiDataPoint.xValue) - 12;
                let exitArrowYValue = isLong ? item.exitBarTiDataPoint.high : item.exitBarTiDataPoint.low;
                let exitArrowTop = isLong ? this.getElementTop(item.exitBarTiDataPoint.high, false) : this.getElementTop(item.exitBarTiDataPoint.low, true);
                
                if (exitArrowYValue > yAxis.min && exitArrowYValue < yAxis.max) {
                    const exitArrowIcon = isLong ? this.getSellIcon() : this.getBuyIcon();
                    const exitArrowKey = StringHelpers.newGuid();
                    const exitArrowG = this.base.tiChartSeries.highStockChart.renderer.g(exitArrowKey).attr({
                        zIndex: 13
                    });
    
                    renderer.image(exitArrowIcon, exitArrowLeft, exitArrowTop, 24, 24)
                        .attr({
                            zIndex: 9
                        })
                        .add(exitArrowG);
    
                    const exitArrowTitleElement = renderer.createElement('title');
                    exitArrowTitleElement.add(exitArrowG);
    
                    try {
                        const existingExitArrowIndex = this.arrows.findIndex(x => x.id == exitArrowKey);
                        if (existingExitArrowIndex > -1) {
                            this.arrows[existingExitArrowIndex].g?.destroy();
    
                            exitArrowG.add();
                            this.arrows[existingExitArrowIndex].g = exitArrowG;
                        } else {
                            exitArrowG.add();
    
                            this.arrows.push({
                                id: exitArrowKey,
                                g: exitArrowG
                            })
                        }
                    } catch { }
    
                    setTimeout(() => {
    
                        const arrow = this.base.chartWrapper.querySelector('.highcharts-' + exitArrowKey);
                        const title = this.base.chartWrapper.querySelector('.highcharts-' + exitArrowKey + ' title');
                        if (arrow) {
                            arrow.addEventListener('mouseenter', () => {
                                this.mouseHover = true;
                            });
                            arrow.addEventListener('mouseleave', () => {
                                clearTimeout(this.mouseHoverTimer)
                                this.mouseHoverTimer = setTimeout(() => {
                                    this.mouseHover = false;
                                    this.base.tiChartSeries.preventDragging = false;
                                }, 1500);
                            });
                        }
                        if (title) {
                            // Add a Sell Tooltip - "Sell @ 3.00, on 7/27/2024 3:00PM, +24.24% profit"
                            title.innerHTML = item.exitBarTooltip;
                        }
                    }, 500);
                }
            }

            item.added = true;
        }

        (this.base.getComponentsByType(EMACloudBuySellSummaryPopupComponent)?.[0] as EMACloudBuySellSummaryPopupComponent)?.setData(this.reportType, tradeItems, totalProfit);
    }

    private calculateLongEntryExitItems() {
        this.longEntryExitItems = [];
        this.longEntryExitTotalProfit = 0;

        const emaCloudSeriesResult = this.getEMACloudTwoSeries();
        if (!emaCloudSeriesResult) return;

        const emaCloudTwo = emaCloudSeriesResult;

        const arrows = this.getEMACloudArrowsForLong(emaCloudTwo);

        const series = this.base.tiChartSeries.highStockChart?.series?.[0];
        if (!series) {
            return;
        }

        for (let i = 0; i < arrows.pointsToAddEnterArrow.length; i++) {
            const p = arrows.pointsToAddEnterArrow[i];
            if (!p) continue;

            const tiDataPoint = this.base.tiChartSeries.tiChartPoints?.find((x: TIChartPointModel) => x.xValue == p.x);
            if (!tiDataPoint) continue;

            const emaCloudTwoPoint = emaCloudTwo?.points?.find(x => x.xValue == p.x);
            if (!emaCloudTwoPoint) continue;

            const emaCloudTwoValue = emaCloudTwoPoint.value;

            const shouldDisplayEntryArrow = this.closeIsAboveCloud2(tiDataPoint, emaCloudTwoValue);
            if (shouldDisplayEntryArrow && series.processedXData.some((x: number) => x == p.x)) {
                const openProfit = this.getLongOpenProfit(tiDataPoint);

                const buyBarClose = FormatHelpers.numberToString(tiDataPoint.close);
                const buyBarTimeFormat = TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe) ? 'mm/dd/yyyy HH:MM tt' : 'mm/dd/yyyy';
                const buyBarTime = DateHelpers.format(buyBarTimeFormat, tiDataPoint.start);
                const buyBarTooltip = `Long Entry @ ${buyBarClose} on ${buyBarTime}`;

                const buySellItem: BuySellItem = {
                    index: i,
                    entryBarTiDataPoint: tiDataPoint,
                    entryBarDate: tiDataPoint.start,
                    entryBarPrice: tiDataPoint.close,
                    entryBarTooltip: buyBarTooltip,
                    exitBarDate: null,
                    exitBarPrice: null,
                    exitBarTooltip: '',
                    exitBarTiDataPoint: null,
                    profitPercentage: null,
                    openProfitPercentage: openProfit,
                    added: false,
                    isLong: true
                };

                let pointInd = -1;
                for (let i = 0; i < arrows.pointsToAddExitArrow.length; i++) {
                    const p = arrows.pointsToAddExitArrow[i];
                    if (p && p.x >= tiDataPoint.xValue) {
                        pointInd = i;
                        break;
                    }
                }

                if (!arrows.pointsToAddExitArrow[pointInd]) {
                    this.longEntryExitTotalProfit += parseFloat(buySellItem.openProfitPercentage.toFixed(2));
                    this.longEntryExitItems.push(buySellItem);
                    continue;
                }

                const exitTiDataPoint = this.base.tiChartSeries.tiChartPoints?.find((x: TIChartPointModel) => x.xValue == arrows.pointsToAddExitArrow[pointInd].x);
                if (!exitTiDataPoint) continue;

                // Sell Profit = ((BuyBar.Close - SellBar.Close)/BuyBar.Close) * 100
                let profit = '';

                const exitProfitPercentage = this.getLongProfit(exitTiDataPoint, buySellItem.entryBarPrice);
                const exitProfitPercentageString = FormatHelpers.numberToStringThousandSeparators(Math.abs(exitProfitPercentage), true, 2);
                profit = `, ${exitProfitPercentage == 0 || exitProfitPercentageString == '0' ? '' : (exitProfitPercentage > 0 ? '+' : '-')}${exitProfitPercentageString}% profit`;

                const exitClose = FormatHelpers.numberToString(exitTiDataPoint.close);
                const exitTimeFormat = TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe) ? 'mm/dd/yyyy HH:MM tt' : 'mm/dd/yyyy';
                const exitTime = DateHelpers.format(exitTimeFormat, exitTiDataPoint.start);
                const exitTooltip = `Long Exit @ ${exitClose} on ${exitTime}${profit}`;


                buySellItem.exitBarDate = exitTiDataPoint.start;
                buySellItem.exitBarPrice = exitTiDataPoint.close;
                buySellItem.exitBarTiDataPoint = exitTiDataPoint;
                buySellItem.exitBarTooltip = exitTooltip;
                buySellItem.profitPercentage = exitProfitPercentage;
                buySellItem.openProfitPercentage = null;

                this.longEntryExitTotalProfit += parseFloat(buySellItem.profitPercentage.toFixed(2));
                this.longEntryExitItems.push(buySellItem);
            }
        }
    }

    private calculateShortEntryExitItems() {
        this.shortEntryExitItems = [];
        this.shortEntryExitTotalProfit = 0;

        const emaCloudSeriesResult = this.getEMACloudTwoSeries();
        if (!emaCloudSeriesResult) return;

        const emaCloudTwo = emaCloudSeriesResult;

        const arrows = this.getEMACloudArrowsForShort(emaCloudTwo);

        const series = this.base.tiChartSeries.highStockChart?.series?.[0];
        if (!series) {
            return;
        }

        for (let i = 0; i < arrows.pointsToAddEnterArrow.length; i++) {
            const p = arrows.pointsToAddEnterArrow[i];
            if (!p) continue;

            const tiDataPoint = this.base.tiChartSeries.tiChartPoints?.find((x: TIChartPointModel) => x.xValue == p.x);
            if (!tiDataPoint) continue;

            const emaCloudTwoPoint = emaCloudTwo?.points?.find(x => x.xValue == p.x);
            if (!emaCloudTwoPoint) continue;

            const emaCloudTwoValue = emaCloudTwoPoint.value;

            const shouldDisplayEntryArrow = this.closeIsBelowCloud2(tiDataPoint, emaCloudTwoValue);
            if (shouldDisplayEntryArrow && series.processedXData.some((x: number) => x == p.x)) {
                const openProfit = this.getShortOpenProfit(tiDataPoint);

                const buyBarClose = FormatHelpers.numberToString(tiDataPoint.close);
                const buyBarTimeFormat = TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe) ? 'mm/dd/yyyy HH:MM tt' : 'mm/dd/yyyy';
                const buyBarTime = DateHelpers.format(buyBarTimeFormat, tiDataPoint.start);
                const buyBarTooltip = `Short Entry @ ${buyBarClose} on ${buyBarTime}`;

                const buySellItem: BuySellItem = {
                    index: i,
                    entryBarDate: tiDataPoint.start,
                    entryBarPrice: tiDataPoint.close,
                    entryBarTiDataPoint: tiDataPoint,
                    entryBarTooltip: buyBarTooltip,
                    exitBarDate: null,
                    exitBarPrice: null,
                    exitBarTooltip: '',
                    exitBarTiDataPoint: null,
                    profitPercentage: null,
                    openProfitPercentage: openProfit > 100 ? 100 : openProfit,
                    added: false,
                    isLong: false
                };

                let pointInd = -1;
                for (let i = 0; i < arrows.pointsToAddExitArrow.length; i++) {
                    const p = arrows.pointsToAddExitArrow[i];
                    if (p && p.x >= tiDataPoint.xValue) {
                        pointInd = i;
                        break;
                    }
                }

                if (!arrows.pointsToAddExitArrow[pointInd]) {
                    this.shortEntryExitTotalProfit += parseFloat(buySellItem.openProfitPercentage.toFixed(2));
                    this.shortEntryExitItems.push(buySellItem);
                    continue;
                }

                const exitTiDataPoint = this.base.tiChartSeries.tiChartPoints?.find((x: TIChartPointModel) => x.xValue == arrows.pointsToAddExitArrow[pointInd].x);
                if (!exitTiDataPoint) continue;

                // Sell Profit = ((BuyBar.Close - SellBar.Close)/BuyBar.Close) * 100
                let profit = '';

                let exitProfitPercentage = this.getShortProfit(exitTiDataPoint, buySellItem.entryBarPrice);
                exitProfitPercentage = exitProfitPercentage > 100 ? 100 : exitProfitPercentage;
                
                const exitProfitPercentageString = FormatHelpers.numberToStringThousandSeparators(Math.abs(exitProfitPercentage), true, 2);
                profit = `, ${exitProfitPercentage == 0 || exitProfitPercentageString == '0' ? '' : (exitProfitPercentage > 0 ? '+' : '-')}${exitProfitPercentageString}% profit`;

                const exitClose = FormatHelpers.numberToString(exitTiDataPoint.close);
                const exitTimeFormat = TimeframeHelper.timeframeIntraday(this.base.tiChartSeries.currentTimeframe) ? 'mm/dd/yyyy HH:MM tt' : 'mm/dd/yyyy';
                const exitTime = DateHelpers.format(exitTimeFormat, exitTiDataPoint.start);
                const exitTooltip = `Short Exit @ ${exitClose} on ${exitTime}${profit}`;


                buySellItem.exitBarDate = exitTiDataPoint.start;
                buySellItem.exitBarPrice = exitTiDataPoint.close;
                buySellItem.exitBarTiDataPoint = exitTiDataPoint;
                buySellItem.exitBarTooltip = exitTooltip;
                buySellItem.profitPercentage = exitProfitPercentage;
                buySellItem.openProfitPercentage = null;

                this.shortEntryExitTotalProfit += parseFloat(buySellItem.profitPercentage.toFixed(2));
                this.shortEntryExitItems.push(buySellItem);
            }
        }
    }

    private getLongProfit(tiDataPoint: TIChartPointModel, entryPrice: number): number {
        return ((tiDataPoint.close - entryPrice) / entryPrice) * 100;
    }

    private getShortProfit(tiDataPoint: TIChartPointModel, entryPrice: number): number {
        return ((entryPrice - tiDataPoint.close) / tiDataPoint.close) * 100;
    }

    private getLongOpenProfit(tiDataPoint: TIChartPointModel): number {
        return ((this.base.tiChartSeries.lastPrice - tiDataPoint.close) / tiDataPoint.close) * 100;
    }

    private getShortOpenProfit(tiDataPoint: TIChartPointModel): number {
        return ((tiDataPoint.close - this.base.tiChartSeries.lastPrice) / tiDataPoint.close) * 100;
    }

    private closeIsAboveCloud2(tiDataPoint: TIChartPointModel, emaCloudTwoValue: any) {
        return tiDataPoint.close > emaCloudTwoValue;
    }

    private closeIsBelowCloud2(tiDataPoint: TIChartPointModel, emaCloudTwoValue: any) {
        return tiDataPoint.close < emaCloudTwoValue;
    }

    private validatePreviousPointsPositionLong(fastEMA: Array<TIIndicatorPointModel>, slowEMA: Array<TIIndicatorPointModel>, xValue: number): boolean {
        // const totalPreviousBar = 2;
        const totalPreviousBar = 0;
        const startIndex = xValue - totalPreviousBar;
        const endIndex = xValue - 1;
        if (isNaN(startIndex)) {
            return false;
        }

        for (let i = startIndex; i < endIndex; i++) {
            const firstPoint = fastEMA?.find(x => x.xValue == i);
            const secondPoint = slowEMA?.find(x => x.xValue == i);
            const tiDataPoint = this.base.tiChartSeries.tiChartPoints.find(x => x.xValue == i);
            if (!firstPoint || !secondPoint || !tiDataPoint) {
                return false;
            }

            const isUpColor = firstPoint.value < secondPoint.value ? true : false;
            // const isUpColor = tiDataPoint.isUpBar() ? true : false;
            if (!isUpColor) {
                return false;
            }
        }

        return true;
    }

    private validatePreviousPointsPositionShort(fastEMA: Array<TIIndicatorPointModel>, slowEMA: Array<TIIndicatorPointModel>, xValue: number): boolean {
        // const totalPreviousBar = 2;
        const totalPreviousBar = 0;
        const startIndex = xValue - totalPreviousBar;
        const endIndex = xValue - 1;
        if (isNaN(startIndex)) {
            return false;
        }

        for (let i = startIndex; i < endIndex; i++) {
            const firstPoint = fastEMA?.find(x => x.xValue == i);
            const secondPoint = slowEMA?.find(x => x.xValue == i);
            const tiDataPoint = this.base.tiChartSeries.tiChartPoints.find(x => x.xValue == i);
            if (!firstPoint || !secondPoint || !tiDataPoint) {
                return false;
            }

           const isUpColor = firstPoint.value < secondPoint.value ? true : false;
        //    const isUpColor = tiDataPoint.isUpBar() ? true : false;
            if (isUpColor) {
                return false;
            }
        }

        return true;
    }

    private getElementTop(yValue: number, buy: boolean): number {
        const yAxis = this.base.tiChartSeries.highStockChart?.yAxis?.[0];
        if (!yAxis) {
            return -999;
        }


        const chartExtraSmall = this.base.element.parentElement?.containClass('extra-small');
        let top = yAxis?.toPixels(yValue, true);
        if (!chartExtraSmall) {
            if (this.base.element.containClass('one')) {
                top += 36;
            } else {
                top += 58;
            }

            top -= 12;
            if (!this.base.marketingWebChartMode) {
                if (!buy) {
                    top -= 12;
                }
            } else {
                if (!buy) {
                    top -= 6;
                } else {
                    top += 6;
                }
            }
        } else {
            top += 6;
        }

        if (buy) {
            top += 12;
        } else {
            top -= 12;
        }

        return top;
    }

    private removeAllArrows() {
        if (this.arrows?.length) {
            for (const arrow of this.arrows) {
                try {
                    arrow.g?.destroy();
                    arrow.g = null;;
                } catch { }
            }
        }

        this.arrows = [];
        this.shortEntryExitItems = [];
        this.longEntryExitItems = [];
        this.reportType = null;
        this.longEntryExitTotalProfit = 0;
        this.shortEntryExitTotalProfit = 0;
    }

    private getEMACloudArrowsForLong(emaCloudTwo: TIIndicatorDataModel): { goodReversalPoints: Array<any>, badReversalPoints: Array<any>, pointsToAddEnterArrow: Array<any>, pointsToAddExitArrow: Array<any>, pointsWithInFiveBars: Array<any> } {
        const goodReversalPoints: Array<any> = [];
        const badReversalPoints: Array<any> = [];
        const pointsToAddEnterArrow: Array<any> = [];
        const pointsToAddExitArrow: Array<any> = [];
        const pointsWithInFiveBars: Array<any> = [];

        if (!emaCloudTwo) return { goodReversalPoints: goodReversalPoints, badReversalPoints: badReversalPoints, pointsToAddEnterArrow: pointsToAddEnterArrow, pointsToAddExitArrow: pointsToAddExitArrow, pointsWithInFiveBars: pointsWithInFiveBars }

        const fastEMACloudTwoDataPoints = emaCloudTwo.points;
        const slowEMACloudTwoDataPoints = emaCloudTwo.childIndicators?.[0].points ?? [];

        let p1Valid: boolean | null = null;
        for (let i = 0; i < fastEMACloudTwoDataPoints.length; i++) {
            const p1 = fastEMACloudTwoDataPoints?.[i];
            const p2 = slowEMACloudTwoDataPoints?.[i];

            if (!p1 || !p2) continue;

            if (p1Valid == null) {
                p1Valid = p1.value > p2.value;
                continue;
            }

            const shouldEnter = p1.value > p2.value && p1Valid == false;
            const shouldExit = p1.value < p2.value && p1Valid == true;
            // if (shouldEnter && shouldEnterNotWithInFiveBar) {
            //     p1Valid = true;
            //     pointsToAddEnterArrow.push({ x: p1.xValue });
            // } else if (shouldExit && shouldExitNotWithInFiveBar) {
            //     p1Valid = false;
            //     pointsToAddExitArrow.push({ x: p1.xValue });
            // }

            if (shouldEnter) {
                p1Valid = true;
                goodReversalPoints.push({ x: p1.xValue });

                const shouldEnterValid = this.validatePreviousPointsPositionLong(fastEMACloudTwoDataPoints, slowEMACloudTwoDataPoints, p1.xValue);
                if (shouldEnterValid) {
                    pointsToAddEnterArrow.push({ x: p1.xValue });
                } else {
                    pointsWithInFiveBars.push({ x: p1.xValue });
                }
            } else if (shouldExit) {
                p1Valid = false;
                badReversalPoints.push({ x: p1.xValue });

                pointsToAddExitArrow.push({ x: p1.xValue });

                // const shouldExitValid = this.validatePreviousPointsPosition(pointsToAddExitArrow, p1.xValue);
                // if (shouldExitValid) {
                //     pointsToAddExitArrow.push({ x: p1.xValue });
                // } else {
                //     pointsWithInFiveBars.push({ x: p1.xValue });
                // }
            }
        }

        return { goodReversalPoints: goodReversalPoints, badReversalPoints: badReversalPoints, pointsToAddEnterArrow: pointsToAddEnterArrow, pointsToAddExitArrow: pointsToAddExitArrow, pointsWithInFiveBars: pointsWithInFiveBars }
    }

    private getEMACloudArrowsForShort(emaCloudTwo: any): { goodReversalPoints: Array<any>, badReversalPoints: Array<any>, pointsToAddEnterArrow: Array<any>, pointsToAddExitArrow: Array<any>, pointsWithInFiveBars: Array<any> } {
        const goodReversalPoints: Array<any> = [];
        const badReversalPoints: Array<any> = [];
        const pointsToAddEnterArrow: Array<any> = [];
        const pointsToAddExitArrow: Array<any> = [];
        const pointsWithInFiveBars: Array<any> = [];

        if (!emaCloudTwo) return { goodReversalPoints: goodReversalPoints, badReversalPoints: badReversalPoints, pointsToAddEnterArrow: pointsToAddEnterArrow, pointsToAddExitArrow: pointsToAddExitArrow, pointsWithInFiveBars: pointsWithInFiveBars }

        const fastEMACloudTwoDataPoints = emaCloudTwo.points;
        const slowEMACloudTwoDataPoints = emaCloudTwo.childIndicators?.[0].points ?? [];

        let p1Valid: boolean | null = null;
        for (let i = 0; i < fastEMACloudTwoDataPoints.length; i++) {
            const p1 = fastEMACloudTwoDataPoints?.[i];
            const p2 = slowEMACloudTwoDataPoints?.[i];

            if (!p1 || !p2) continue;

            if (p1Valid == null) {
                p1Valid = p1.value < p2.value;
                continue;
            }

            // const shouldEnterNotWithInFiveBar = this.validatePreviousPointsPosition(pointsToAddEnterArrow, p1.xValue);
            // const shouldExitNotWithInFiveBar = this.validatePreviousPointsPosition(pointsToAddExitArrow, p1.xValue);

            const shouldEnter = p1.value < p2.value && p1Valid == false;
            const shouldExit = p1.value > p2.value && p1Valid == true;
            // if (shouldEnter && shouldEnterNotWithInFiveBar) {
            //     p1Valid = true;
            //     pointsToAddEnterArrow.push({ x: p1.xValue });
            // } else if (shouldExit && shouldExitNotWithInFiveBar) {
            //     p1Valid = false;
            //     pointsToAddExitArrow.push({ x: p1.xValue });
            // }


            if (shouldEnter) {
                p1Valid = true;
                goodReversalPoints.push({ x: p1.xValue });

                const shouldEnterNotWithInFiveBar = this.validatePreviousPointsPositionShort(fastEMACloudTwoDataPoints, slowEMACloudTwoDataPoints, p1.xValue);
                if (shouldEnterNotWithInFiveBar) {
                    pointsToAddEnterArrow.push({ x: p1.xValue });
                } else {
                    pointsWithInFiveBars.push({ x: p1.xValue });
                }
            } else if (shouldExit) {
                p1Valid = false;
                badReversalPoints.push({ x: p1.xValue });

                pointsToAddExitArrow.push({ x: p1.xValue });
                // if (shouldExitNotWithInFiveBar) {
                //     pointsToAddExitArrow.push({ x: p1.xValue });
                // } else {
                //     pointsWithInFiveBars.push({ x: p1.xValue });
                // }
            }
        }

        return { goodReversalPoints: goodReversalPoints, badReversalPoints: badReversalPoints, pointsToAddEnterArrow: pointsToAddEnterArrow, pointsToAddExitArrow: pointsToAddExitArrow, pointsWithInFiveBars: pointsWithInFiveBars }
    }

    private getEMACloudTwoSeries(): TIIndicatorDataModel | null {
        let emaCloudTwo: TIIndicatorDataModel | null = null;
        let emaClounds: Array<TIIndicatorDataModel> = [];
        for (const ind of this.base.tiChartSeries.tiIndicatorsData) {
            if (ind.name?.toLowerCase()?.indexOf('emacloud') > -1 && ind.points?.length) {
                emaClounds.push(ind);
            }
        }

        emaCloudTwo = emaClounds[emaClounds.length - 2];

        if (!emaCloudTwo) return null;

        return emaCloudTwo;
    }

    private getBuyIcon(): string {
        // SVG source code
        // const greenColor = '#3a8e22';
        const blueColor = '#006598';
        const svgSource = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44.48 50" width="24px" height="24px">
                <g>
                    <polygon fill="${blueColor}" points="0 21.37 0 50 44.48 50 44.48 21.37 22.24 0 0 21.37"/>
                    <path fill="#fff" d="M41.61,47.12H2.88v-24.53L22.24,3.99l19.37,18.61v24.53ZM4.88,45.12h34.73v-21.68L22.24,6.76,4.88,23.45v21.68Z"/>
                </g>
                <path fill="#fff" d="M15.82,39.11v-18h6.17c2.04,0,3.59.41,4.65,1.22s1.59,2.03,1.59,3.65c0,.82-.22,1.57-.67,2.23s-1.1,1.17-1.95,1.53c.97.26,1.72.76,2.26,1.49s.8,1.61.8,2.63c0,1.69-.54,2.99-1.63,3.89s-2.64,1.36-4.65,1.36h-6.56ZM18.95,28.69h3.07c.97,0,1.73-.22,2.28-.66s.82-1.07.82-1.87c0-.89-.25-1.53-.76-1.93s-1.3-.59-2.37-.59h-3.04v5.06ZM18.95,30.99v5.62h3.47c.98,0,1.75-.24,2.3-.73s.83-1.16.83-2.03c0-1.87-.96-2.83-2.87-2.87h-3.73Z"/>
            </svg>
        `;

        // Create a data URL from the SVG source code
        const dataUrl = 'data:image/svg+xml,' + encodeURIComponent(svgSource);
        return dataUrl;
    }

    private getSellIcon(): string {
        // SVG source code
        // const redColor = '#ed1c24';
        const orangeColor = '#F88605';
        const svgSource = `
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 44.48 50" width="24px" height="24px">
                <g>
                    <polygon fill="${orangeColor}" points="44.48 28.63 44.48 0 0 0 0 28.63 22.24 50 44.48 28.63"/>
                    <path fill="#fff" d="M22.24,46.01L2.88,27.4V2.87h38.73v24.53l-19.37,18.61ZM4.88,26.55l17.37,16.69,17.37-16.69V4.87H4.88v21.68Z"/>
                </g>
                <path fill="#fff" d="M25.74,24.13c0-.77-.27-1.36-.81-1.78s-1.52-.84-2.93-1.26-2.53-.9-3.37-1.42c-1.6-1-2.39-2.31-2.39-3.92,0-1.41.58-2.57,1.73-3.49s2.65-1.37,4.48-1.37c1.22,0,2.31.22,3.26.67s1.7,1.09,2.25,1.92.82,1.75.82,2.76h-3.03c0-.91-.29-1.63-.86-2.15s-1.39-.78-2.46-.78c-.99,0-1.77.21-2.32.64s-.82,1.02-.82,1.78c0,.64.3,1.18.89,1.61s1.57.85,2.94,1.25,2.46.87,3.28,1.38,1.43,1.11,1.82,1.78.58,1.45.58,2.35c0,1.46-.56,2.62-1.68,3.48s-2.64,1.29-4.55,1.29c-1.27,0-2.43-.23-3.5-.7s-1.89-1.12-2.48-1.94-.88-1.79-.88-2.89h3.04c0,.99.33,1.76.99,2.31s1.6.82,2.83.82c1.06,0,1.85-.21,2.39-.64s.8-1,.8-1.7Z"/>
            </svg>
        `;

        // Create a data URL from the SVG source code
        const dataUrl = 'data:image/svg+xml,' + encodeURIComponent(svgSource);
        return dataUrl;
    }
}